import httpClient from '@/httpClient/httpClient';
import {
  ResponseBody,
  RootState,
  ResponseDataProvider,
  DataProvider,
  PaginationData,
  SspStatistic,
  StatisticsState,
  DetailSspStatistic,
  DetailSspStatisticRow,
  CreativeStatistic,
  LeadStatistic,
  SiteStatistic,
} from '@/types';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import ResponseDataValue, {
  ResponseDataProviderValue,
  ResponseDataProviderWithTotalValue,
} from '@/utils/ResponseDataValue';
import { Notification } from 'uiv';
import i18n from '@/i18n';

const REQUEST_ERROR = i18n.t('notifications.errorRequest');

const state: StatisticsState = {
  ssp: {
    paginationData: null as PaginationData | null,
    data: [] as Array<SspStatistic>,
    total: undefined,
  },
  detailSsp: [],
  campaign: {
    paginationData: null as PaginationData | null,
    data: [] as Array<SspStatistic>,
    total: undefined,
  },
  creative: {
    paginationData: null as PaginationData | null,
    data: [] as Array<CreativeStatistic>,
    total: undefined,
  },
  lead: {
    paginationData: null as PaginationData | null,
    data: [] as Array<LeadStatistic>,
    total: undefined,
  },
  site: {
    paginationData: null as PaginationData | null,
    data: [] as Array<SiteStatistic>,
  },
};
const mutations: MutationTree<StatisticsState> = {
  setSsp(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.ssp.paginationData = data.paginationData;
    state.ssp.data = data.data;
    state.ssp.total = data.total;
  },
  setDetailSsp(state, data: DetailSspStatisticRow): void {
    const model = state.detailSsp.find((item: DetailSspStatisticRow) => {
      item.id = data.id;
    });
    if (model) {
      model.data = data.data;
    } else {
      state.detailSsp.push(data);
    }
  },
  setCampaign(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.campaign.paginationData = data.paginationData;
    state.campaign.data = data.data;
    state.campaign.total = data.total;
  },
  setCreative(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.creative.paginationData = data.paginationData;
    state.creative.data = data.data;
    state.creative.total = data.total;
  },
  setLead(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.lead.paginationData = data.paginationData;
    state.lead.data = data.data;
    state.lead.total = data.total;
  },
  setSite(state, data: DataProvider): void {
    if (!data.paginationData || !data.paginationData?.currentPage) {
      return;
    }
    state.site.paginationData = data.paginationData;
    state.site.data = data.data;
  },
};

const getters: GetterTree<StatisticsState, RootState> = {
  getDetailSsp:
    (state) =>
    (id: number): Array<DetailSspStatistic> => {
      const model = state.detailSsp.find((item: DetailSspStatisticRow) => {
        return (item.id = id);
      });
      if (model) {
        return model.data;
      }
      return [];
    },
  getCreativesStatistic(state) {
    return state.creative;
  },
};

const actions: ActionTree<StatisticsState, RootState> = {
  async sspStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get('/statistics/ssp', {
        params: Object.assign({}, data.searchData, {
          fields: data.columns.join(','),
        }),
      });
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderWithTotalValue(response, true);
      commit('setSsp', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async sspStatisticsDetail({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/ssp/' + data.id,
        {
          params: data.params,
        }
      );
      commit('setDetailSsp', { id: data.id, data: response.data });
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async campaignStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/campaign',
        {
          params: Object.assign({}, data.searchData, {
            fields: data.columns.join(','),
          }),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderWithTotalValue(response, true);
      commit('setCampaign', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async detailCampaignStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/detail-campaign',
        {
          params: Object.assign({}, data.periods, {
            fields: data.columns.join(','),
          }),
        }
      );
      const responseDataProvider: ResponseDataProvider = ResponseDataValue(
        response,
        true
      );
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async loadCreativeStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/creative',
        {
          params: Object.assign({}, data.searchData, {
            fields: data.columns.join(','),
          }),
        }
      );

      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderWithTotalValue(response, true);
      commit('setCreative', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async leadStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/lead',
        {
          params: Object.assign({}, data.searchData, {
            fields: data.columns.join(','),
          }),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderWithTotalValue(response, true);
      commit('setLead', responseDataProvider.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async siteStatistics({ commit, dispatch }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/site',
        {
          params: Object.assign({}, data.searchData, {
            fields: data.columns.join(','),
          }),
        }
      );
      const responseDataProvider: ResponseDataProvider =
        ResponseDataProviderValue(response, true);

      commit('setSite', responseDataProvider.data);
      dispatch('campaignSites/setDomains', responseDataProvider.data.data);
      return responseDataProvider;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async exportSspStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/ssp/export',
        {
          params: data,
        }
      );
      return response.data;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async exportCampaignStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/campaign/export',
        {
          params: data,
        }
      );
      return response.data;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async exportCreativeStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/creative/export',
        {
          params: data,
        }
      );
      return response.data;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async exportLeadStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/lead/export',
        {
          params: data,
        }
      );
      return response.data;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
  async exportSiteStatistics({ commit }, data) {
    try {
      const response: ResponseBody = await httpClient().get(
        '/statistics/site/export',
        {
          params: data,
        }
      );
      return response.data;
    } catch (e) {
      Notification.notify.error({
        title: REQUEST_ERROR,
      });
    }
  },
};

export default {
  state,
  mutations,
  getters,
  actions,
};
